import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import "@/App.css";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import CatalogSection from "@/components/CatalogSection";
import MatcherSection from "@/components/MatcherSection";
import ProcessSection from "@/components/ProcessSection";
import ManifestoSection from "@/components/ManifestoSection";
import WorkshopSection from "@/components/WorkshopSection";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import AuthModal from "@/components/AuthModal";
import PasswordResetPage from "@/components/PasswordResetPage";
import AccountPage from "@/components/AccountPage";
import LocksPage from "@/components/LocksPage";
import AdminPage from "@/components/AdminPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PaymentResult({ status }) {
  const [payment, setPayment] = useState({ state: "checking", label: "Checking your order" });
  const navigate = useNavigate();
  useEffect(() => {
    if (status === "cancel") {
      setPayment({ state: "cancelled", label: "Checkout was cancelled" });
      return;
    }
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setPayment({ state: "missing", label: "No order session found" });
      return;
    }
    let active = true;
    const check = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!active) return;
        if (response.data.payment_status === "paid") {
          setPayment({ state: "paid", label: "Order acknowledged" });
        } else {
          setPayment({ state: "pending", label: "Payment is still confirming" });
        }
      } catch {
        if (active) setPayment({ state: "error", label: "We could not read the order status" });
      }
    };
    check();
    return () => { active = false; };
  }, [status]);
  return (
    <main className="result-page" data-testid="payment-result-page">
      <div className={`result-mark result-mark-${payment.state}`}>{payment.state === "paid" ? "✓" : "↗"}</div>
      <p className="eyebrow">BICYCLE PARTS DIRECT / ORDER DESK</p>
      <h1 data-testid="payment-result-heading">{payment.label}</h1>
      <p>{payment.state === "paid" ? "Your parts are reserved in the order queue. Stripe has confirmed the payment." : "You can return to the catalogue whenever you are ready to finish the order."}</p>
      <button className="button button-primary" data-testid="payment-result-return-btn" onClick={() => navigate("/")}>Return to catalogue</button>
    </main>
  );
}

function Home() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("register");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [catalogResponse, userResponse] = await Promise.all([
          axios.get(`${API}/catalog`),
          axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setProducts(catalogResponse.data.products);
        setCategories(catalogResponse.data.categories);
        setUser(userResponse.data);
      } catch {
        toast.error("The catalogue is taking a moment to warm up.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!products.length) return;
    const saved = window.localStorage.getItem("bpd-repeat-order");
    if (!saved) return;
    const requested = JSON.parse(saved);
    const restored = requested.map((item) => {
      const product = products.find((candidate) => candidate.lookup_key === item.lookup_key);
      return product ? { product, quantity: item.quantity } : null;
    }).filter(Boolean);
    window.localStorage.removeItem("bpd-repeat-order");
    if (restored.length) {
      setCart(restored);
      toast.success("Previous order quantities loaded into your cart");
    }
  }, [products]);

  useEffect(() => {
    const saved = window.localStorage.getItem("bpd-cart-add");
    if (!saved) return;
    const item = JSON.parse(saved);
    window.localStorage.removeItem("bpd-cart-add");
    setCart((current) => {
      const existing = current.find((entry) => entry.product.lookup_key === item.product.lookup_key);
      return existing ? current.map((entry) => entry.product.lookup_key === item.product.lookup_key ? { ...entry, quantity: entry.quantity + item.quantity } : entry) : [...current, item];
    });
    toast.success(`${item.product.name} added to your bench cart`);
  }, []);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("auth") !== "login") return;
    setAuthMode("login");
    setAuthOpen(true);
    window.history.replaceState({}, "", "/");
  }, []);

  const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);
  const addToCart = (product, quantity = 1) => {
    setCart((current) => {
      const existing = current.find((item) => item.product.lookup_key === product.lookup_key);
      if (existing) return current.map((item) => item.product.lookup_key === product.lookup_key ? { ...item, quantity: item.quantity + quantity } : item);
      return [...current, { product, quantity }];
    });
    toast.success(`${product.name} added to your bench cart`);
  };
  const updateQuantity = (lookupKey, quantity) => {
    setCart((current) => quantity < 1 ? current.filter((item) => item.product.lookup_key !== lookupKey) : current.map((item) => item.product.lookup_key === lookupKey ? { ...item, quantity } : item));
  };
  const openAccount = (mode = "register") => { setAuthMode(mode); setAuthOpen(true); };
  const checkout = async () => {
    if (!cart.length || checkoutBusy) return;
    setCheckoutBusy(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, { items: cart.map((item) => ({ lookup_key: item.product.lookup_key, quantity: item.quantity })), origin_url: window.location.origin }, { withCredentials: true });
      window.location.assign(response.data.checkout_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout could not be started. Please try again.");
      setCheckoutBusy(false);
    }
  };
  const addBundle = (bundle) => bundle.forEach((product) => addToCart(product));

  return (
    <div className="app-shell">
      <Header cartCount={cartCount} user={user} onCart={() => setCartOpen(true)} onAccount={() => user ? navigate("/account") : openAccount("login")} />
      <main>
        <Hero onExplore={() => document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" })} onAccount={() => openAccount("register")} />
        <Marquee />
        <CatalogSection products={products} categories={categories} loading={loading} onAdd={addToCart} />
        <MatcherSection products={products} onAddBundle={addBundle} />
        <ProcessSection />
        <ManifestoSection />
        <WorkshopSection />
      </main>
      <Footer onAccount={() => openAccount("register")} />
      <CartDrawer open={cartOpen} cart={cart} onClose={() => setCartOpen(false)} onUpdate={updateQuantity} onCheckout={checkout} busy={checkoutBusy} />
      <AuthModal open={authOpen} mode={authMode} user={user} onClose={() => setAuthOpen(false)} onMode={setAuthMode} onSignOut={() => setUser(null)} onSuccess={(account) => { setUser(account); setAuthOpen(false); toast.success(`Welcome to the bench, ${account.name}`); }} />
      <Toaster position="bottom-right" toastOptions={{ className: "bpd-toast" }} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/payment/success" element={<PaymentResult status="success" />} />
        <Route path="/payment/cancel" element={<PaymentResult status="cancel" />} />
        <Route path="/reset-password" element={<PasswordResetPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/locks" element={<LocksPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
